import React, { useEffect, useState, useMemo } from 'react'
import log from 'loglevel'
import { RISK_MATH } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { Pill, PillSelection } from 'src/components/Reusable/Pill'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { Risk } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { getRiskMathField, updatedRiskRpcData } from '../risks.helpers'
import {
  RiskFields,
  RISK_MATH_DROPDOWN,
  RISK_MATH_LABEL,
  RISK_MATH_BG,
  RISK_MATH_VALUES,
} from '../risks.constants'
import { useCreateOrUpdateRisks, useRisk } from '../risks.hooks'
import { RiskPointLabel, RiskPillLabel } from '../risks.styles'
export const RisksMathMenu = ({
  riskId,
  field,
  initValue,
  maxValue,
  isReadOnly,
  showValue,
  $height,
}: {
  riskId: string
  field: RiskFields
  maxValue?: RISK_MATH
  initValue?: RISK_MATH
  isReadOnly?: boolean
  showValue?: boolean
  $height?: string
}): JSX.Element => {
  const risk = useRisk(riskId)
  const riskObj = risk instanceof Risk ? risk.toObject() : undefined
  const canUpdateRisk = useCanPerformAction<Risk.AsObject>(
    CAPABILITY.CAN_UPDATE_RISKS,
    riskObj,
  )
  const updateRisk = useCreateOrUpdateRisks()
  const [riskMath, setRiskMath] = useState<RISK_MATH>(
    initValue || RISK_MATH.NOT_SET,
  )
  // Residual Risk can only be set to a value less than or equal to the Inherent Risk
  const values = maxValue
    ? RISK_MATH_DROPDOWN.filter((item) => item.value <= maxValue)
    : RISK_MATH_DROPDOWN

  const selectedOption = useMemo(() => {
    return values.find((option) => option.value === riskMath)
  }, [riskMath, values])

  useEffect(() => {
    // Acts as a chip in read-only mode by using these props
    if ((initValue || initValue === RISK_MATH.NOT_SET) && isReadOnly) {
      setRiskMath(initValue)
      return
    }
    if (!risk) {
      return
    }
    setRiskMath(getRiskMathField(risk, field))
  }, [field, initValue, isReadOnly, risk, riskMath])

  const disableToggle = !canUpdateRisk || isReadOnly

  const selectRiskMath = async (newRiskMath: RISK_MATH) => {
    try {
      if (disableToggle || !risk) {
        return
      }
      await updateRisk([updatedRiskRpcData(risk, field, newRiskMath)])
    } catch (err) {
      log.error(`Failed to update risk ${field} for riskId: ${riskId}:`, err)
    }
    return
  }

  return (
    <>
      {disableToggle ? (
        <Pill
          backgroundColor={RISK_MATH_BG[riskMath]}
          label={
            <FlexRow
              justify={FlexAlign.FLEX_START}
              align={FlexAlign.CENTER}
              gap={8}
            >
              <RiskPillLabel>{RISK_MATH_LABEL[riskMath]}</RiskPillLabel>
              {showValue && (
                <RiskPointLabel>
                  {RISK_MATH_VALUES[riskMath]} Points
                </RiskPointLabel>
              )}
            </FlexRow>
          }
          $width="100%"
          $height={$height}
        />
      ) : (
        <PillSelection
          initialSelectedOption={selectedOption}
          options={values}
          onSelectionChange={(selected) => {
            const value = selected?.value
            selectRiskMath(
              typeof value === 'number'
                ? (value as RISK_MATH)
                : RISK_MATH.NOT_SET,
            )
          }}
          renderLabel={
            showValue
              ? (option) =>
                  option ? (
                    <FlexRow
                      justify={FlexAlign.FLEX_START}
                      align={FlexAlign.CENTER}
                      gap={8}
                    >
                      {option.name}
                      <RiskPointLabel>
                        {RISK_MATH_VALUES[option.value as RISK_MATH]} Points
                      </RiskPointLabel>
                    </FlexRow>
                  ) : null
              : undefined
          }
          $width="100%"
          $height="40px"
        />
      )}
    </>
  )
}
